/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useMemo, useState } from 'react';

import { Button, FullscreenDialog, Typography2 } from '@coursera/cds-core';

import TrackedButton from 'bundles/page/components/TrackedButton';

import _t from 'i18n!nls/degree-home';

import MultipleCoursesSelectView from './MultipleCoursesSelectView';
import SessionSelectionIndicator from './SessionSelectionIndicator';
import SingleCourseSelectView from './SingleCourseSelectView';
import type { EnrollableCourse as Course, EnrollableSession as Session } from './types/EnrollableCourse';
import type { EnrollingCourse } from './types/EnrollingCourse';

const SINGLE_COURSE_ENROLL_TRACKING = 'single_course_enroll';
const MULTIPLE_COURSES_PARTIAL_ENROLL_TRACKING = 'multiple_courses_partial_enroll';
const MULTIPLE_COURSES_FULL_ENROLL_TRACKING = 'multiple_courses_full_enroll';

const styles = {
  container: css`
    overflow-y: auto;
  `,
  enrollButton: () => css`
    margin-right: var(--cds-spacing-100);
  `,
  dialogHeadingGroup: css`
    padding-bottom: 0;
  `,
  contentDescription: css`
    padding-bottom: 3rem;
  `,
  radioGroupCourseName: css`
    padding-bottom: 1rem;
  `,
};

type PickStartDateViewProps = {
  onEnroll: (enrollingCourses: EnrollingCourse[]) => void;
  onClose: () => void;
  courses: Course[];
  enrollingCourseId?: string;
};

export const PickStartDateView = ({ onEnroll, onClose, courses, enrollingCourseId }: PickStartDateViewProps) => {
  const { HeadingGroup, Content, Actions } = FullscreenDialog;
  const isMultipleCourses = courses.length > 1;
  const [enrollingSessionsMap, setEnrollingSessionsMap] = useState<Record<string, Session>>({});
  const enrollingSessionsCount = useMemo(() => {
    return Object.values(enrollingSessionsMap).length;
  }, [enrollingSessionsMap]);

  const enrollButtonTrackingName = useMemo(() => {
    if (!isMultipleCourses) return SINGLE_COURSE_ENROLL_TRACKING;
    if (enrollingSessionsCount === courses.length) return MULTIPLE_COURSES_FULL_ENROLL_TRACKING;
    return MULTIPLE_COURSES_PARTIAL_ENROLL_TRACKING;
  }, [enrollingSessionsCount, isMultipleCourses, courses]);

  const onSessionSelect = (courseId: string, selectedSession: Session) => {
    setEnrollingSessionsMap((prevState) => {
      const newState = { ...prevState };
      newState[courseId] = selectedSession;
      return newState;
    });
  };

  const onEnrollHandler = () => {
    const enrollingCourses: EnrollingCourse[] = [];
    courses.forEach((course: Course) => {
      const { id, name, slug } = course;
      if (enrollingSessionsMap[id]) {
        enrollingCourses.push({
          id,
          name,
          session: enrollingSessionsMap[id],
          slug,
        } as EnrollingCourse);
      }
    });
    onEnroll(enrollingCourses);
  };
  return (
    <>
      <HeadingGroup heading={_t('Pick a start date')} css={styles.dialogHeadingGroup}>
        <SessionSelectionIndicator selected={enrollingSessionsCount} total={courses.length} />
      </HeadingGroup>
      <Content id="enrollmentDialogContent" css={styles.container} aria-live="polite" aria-atomic="true">
        <Typography2 component="p" variant="bodyPrimary" css={styles.contentDescription}>
          {isMultipleCourses
            ? _t(`The dates you select will determine your assignment deadlines. Taking more
than 1 course at a time will increase your workload for each week.`)
            : _t('The dates you select will determine your assignment deadlines.')}
        </Typography2>
        {isMultipleCourses ? (
          <MultipleCoursesSelectView
            courses={courses}
            onSelectChange={onSessionSelect}
            enrollingCourseId={enrollingCourseId}
          />
        ) : (
          // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
          <SingleCourseSelectView course={courses[0]} onSelectChange={onSessionSelect} />
        )}
      </Content>
      <Actions>
        <Button
          iconPosition="after"
          variant="primary"
          onClick={onEnrollHandler}
          css={styles.enrollButton}
          disabled={enrollingSessionsCount === 0}
          component={TrackedButton}
          trackingName={enrollButtonTrackingName}
          withVisibilityTracking={false}
          requireFullyVisible={false}
          aria-label={_t('Enroll into #{selectedCourses}', {
            selectedCourses:
              enrollingSessionsCount === 1
                ? courses.find((course: Course) => enrollingSessionsMap[course.id])?.name
                : _t('#{amount} courses', { amount: enrollingSessionsCount }),
          })}
        >
          {_t('Enroll')}
        </Button>
        <Button variant="secondary" onClick={onClose}>
          {_t('Cancel')}
        </Button>
      </Actions>
    </>
  );
};

export default PickStartDateView;
